import StepFlowMainStrategy from './StepFlowMainStrategy';
import StepFlowStrategyManager from './StepFlowStrategyManager';
import { hasMatchingArrayElements, isValidLoanAmount } from './util';
import { PI1_STEPS_ORDER, FLOW_ERRORS } from './constants';
import { isValidLoanPurposeValue } from '@shared_modules/hooks/AmountPurpose/utils';

// TODO refactor or think of better way - these are mainly for progress bar
const loanTypeStep = '/loan-type';
const agreementsStep = ['/agreements']; // second loan finishing step
const dmFlowPrefix = 'DM_';
const secondLoanPrefix = '2NDLOAN_';
let primarySteps = [];
let secondarySteps = [];
let primaryStepsPartialPrequal = [];
let primaryStepsDM = [];
let primarySteps2ndLoan = [];
let finalSteps = ['/email', '/password'];
let steps = {};

// export only for testing purposes
export function computeFlow(options) {
  const { context, loanType, ssnRequired, isLoggedIn, errors, checkEmailErrors } = options;
  const { dm = {}, prequal, experiments = {} } = context || {};
  // ssn unhappy path
  if (ssnRequired.primary || ssnRequired.secondary) return 'SSNUNHAPPY';

  if (errors && errors.length > 0) {
    // Borrower gets any error other than LOAN_APP_IN_PROGRESS/REJECTED_BORROWER error and is logged in.
    if (isLoggedIn) {
      if (errors.some(error => ['LOAN_APP_IN_PROGRESS', 'REJECTED_BORROWER'].includes(error.error)))
        return 'CONTINUE_APP';
      return 'ERROR_LOGGED_IN';
    }

    return 'ERROR_LOGGED_OUT';
  }

  if (isLoggedIn && checkEmailErrors && checkEmailErrors.length > 0) {
    if (hasMatchingArrayElements(checkEmailErrors, [FLOW_ERRORS.LOAN_APP_IN_PROGRESS, FLOW_ERRORS.REJECTED_BORROWER]))
      return 'CONTINUE_APP';
  }

  // transform loanType to upperCase, default as INDIVIDUAL if no available
  const loanTypeUpperCase = loanType ? loanType.toUpperCase() : 'INDIVIDUAL';

  // Experian DM
  if (dm.isDm && dm.isExperianDm) {
    // Experian DM with missing PII is treated same as Main flow without loan-type screen
    return dm.hasPartialDMPII ? 'DM_EXPERIAN_PARTIAL_PII' : 'DM_EXPERIAN';
  }

  if (dm.isDm && dm.isProspect) {
    return `${dmFlowPrefix}${loanTypeUpperCase}`;
  }

  // regular DM w/o missing PII (name/address). DM with missing PII is treated same as Main flow
  if (dm.isDm && !dm.isExperianDm && !dm.hasPartialDMPII) return `${dmFlowPrefix}${loanTypeUpperCase}`;
  // second loan borrower (logged in user)
  if (context.isFromSecondLoanFlow) return `${secondLoanPrefix}${loanTypeUpperCase}`;

  // loanIntent page remove if all params are presents
  const haveValidLoanAmountAndPurpose =
    isValidLoanAmount(prequal?.loanAmount) && isValidLoanPurposeValue(prequal?.loanPurpose);
  const isMonthlyPaymentVariant = ['A', 'B'].includes(experiments.PL_PI1_MONTHLY_PAYMENT);
  const isValidFullPrequalScenario = isMonthlyPaymentVariant
    ? haveValidLoanAmountAndPurpose && prequal?.monthlyPayment
    : haveValidLoanAmountAndPurpose;

  if (isValidFullPrequalScenario) {
    return `FULL_PREQUAL_${loanTypeUpperCase}`;
  }

  return `PARTIAL_PREQUAL_${loanTypeUpperCase}`;
}

/**
 * Since we are changing the order of steps in the test, we need to reset the steps for tests to work
 */
function initializeSteps() {
  primarySteps = [
    '/primary-dob',
    '/primary-income',
    '/primary-living',
    '/primary-name',
    '/primary-address',
    '/primary-phone',
  ];
  secondarySteps = [
    '/secondary-name',
    '/secondary-address',
    '/secondary-dob',
    '/secondary-income',
    '/secondary-living',
  ];
  primaryStepsPartialPrequal = [...primarySteps.slice(0, 3), '/loan-intent', ...primarySteps.slice(3)];
  primarySteps2ndLoan = ['/primary-address', '/loan-intent', '/primary-income', '/primary-living']; // note: no phone or dob screen!
  primaryStepsDM = [
    '/primary-address',
    '/primary-phone',
    '/loan-intent',
    '/primary-dob',
    '/primary-income',
    '/primary-living',
  ];
}

function computeSteps(options) {
  let initSteps = [loanTypeStep];

  initializeSteps();

  const flow = computeFlow(options);

  if (!flow.startsWith(dmFlowPrefix) && !flow.startsWith(secondLoanPrefix)) {
    const stepFlowStrategyManager = new StepFlowStrategyManager(options);
    const STEPS_ORDER = PI1_STEPS_ORDER.MAIN;
    stepFlowStrategyManager.strategy = new StepFlowMainStrategy(STEPS_ORDER.PRIMARY, STEPS_ORDER.SECONDARY);
    const stepFlow = stepFlowStrategyManager.calculateSteps(flow);
    return stepFlow;
  }

  if (options.isLoggedIn) {
    finalSteps = [];
  }

  // TODO these flow probably will be moved to step strategy on a future phase.
  switch (flow) {
    case 'DM_EXPERIAN':
      steps = {
        allSteps: [...primaryStepsDM, ...finalSteps],
        primarySteps: [...primaryStepsDM, ...finalSteps],
        secondarySteps: [],
      };
      break;
    case 'DM_EXPERIAN_PARTIAL_PII':
      steps = {
        allSteps: [...primaryStepsPartialPrequal, ...finalSteps],
        primarySteps: [...primaryStepsPartialPrequal, ...finalSteps],
        secondarySteps: [],
      };
      break;
    case 'DM_INDIVIDUAL':
      steps = {
        allSteps: [...initSteps, ...primaryStepsDM, ...finalSteps],
        primarySteps: [...primaryStepsDM, ...finalSteps],
        secondarySteps: [],
      };
      break;
    case 'DM_JOINT':
      steps = {
        allSteps: [...initSteps, ...primaryStepsDM, ...secondarySteps, ...finalSteps],
        primarySteps: [...primaryStepsDM],
        secondarySteps: [...secondarySteps, ...finalSteps],
      };
      break;
    case '2NDLOAN_INDIVIDUAL':
      steps = {
        allSteps: [...initSteps, ...primarySteps2ndLoan, ...agreementsStep],
        primarySteps: [...primarySteps2ndLoan, ...agreementsStep],
        secondarySteps: [],
      };
      break;
    case '2NDLOAN_JOINT':
      steps = {
        allSteps: [...initSteps, ...primarySteps2ndLoan, ...secondarySteps, ...agreementsStep],
        primarySteps: [...primarySteps2ndLoan],
        secondarySteps: [...secondarySteps, ...agreementsStep],
      };
      break;
    default:
      steps = {
        allSteps: [...initSteps, ...primarySteps, ...finalSteps],
        primarySteps: [...primarySteps, ...finalSteps], // steps will show up in primaryProgressBar
        secondarySteps: [], // steps will show up in secondaryProgressBar
      };
      break;
  }

  return steps;
}

export default computeSteps;

require('/var/lib/jenkins/workspace/borrower-apply-ui_Release_0/src/pages/pi1-1QAAT/pi1-1QAAT.scss');